/**
 * Account-deletion tombstone and audit-event schemas plus Firebase paths.
 *
 * The tombstone at `accountDeletions/{uid}` is four things at once:
 *
 * 1. **Lock** — created transactionally when a deletion is requested; a
 *    second start attempt returns the existing state instead of racing.
 * 2. **Checkpoint store** — `completedTasks` lets a resumed job skip
 *    manifest tasks that already finished.
 * 3. **Backup-restore tombstone** — a restore pipeline must re-apply the
 *    deletion for any uid with a tombstone (CCPA 11 CCR § 7022(b); GDPR
 *    "beyond use" guidance).
 * 4. **Compliance audit record** — the append-only `deletionAuditEvents`
 *    subcollection (modeled on `courseBanAudit.ts`) proves the deletion
 *    happened and when, without retaining any PII: uids and op counts
 *    only, never names, emails, or content.
 *
 * Tracking: https://github.com/CodeinPlace/cip/issues/3263
 */
import {z} from 'zod';

import {FirestoreTimestampSchema} from '../firebase/types';
import {DeletionPolicySchema} from './classification';

/**
 * Lifecycle of a tombstone.
 *
 * - `scheduled` — deletion requested and the account is **already locked
 *   out** (Auth disabled, refresh tokens revoked), but the destructive
 *   cascade has not started: it is deferred to the next deletion wave.
 *   No job doc exists yet, so `activeJobId` is null and `scheduledFor`
 *   carries the earliest wave the tombstone is eligible for.
 * - `in-progress` — a job doc exists and the cascade is running, or is
 *   about to. Reached either from `scheduled`, when a deletion wave
 *   claims the tombstone, or directly on an admin run-now request.
 * - `complete` — cascade finished and the residue scan ran every probe
 *   and found nothing.
 * - `failed` — the job hit a terminal error. An admin run-now dispatches
 *   a fresh job against the same tombstone, skipping checkpointed tasks.
 * - `needs-attention` — the cascade could not finish or verify: a task
 *   reported failures, a probe could not run, or the residue scan found
 *   leftovers. Re-run the same way as `failed`.
 * - `cancelled` — a superadmin cancelled the deletion while it was still
 *   `scheduled` and the Auth account was re-enabled; nothing was erased.
 *   Terminal: no wave claims it, and a later request replaces it as if no
 *   tombstone existed. The account is live again, so check
 *   {@link isAccountDeletionInForce} rather than whether this document
 *   exists.
 *
 * **Cadence.** Self-service deletions are executed in waves rather than
 * on request: the request path writes a `scheduled` tombstone and the
 * scheduled orchestrator (`scheduleAccountDeletionBatch` in the
 * `account-deletion` isolated codebase) claims due tombstones, flipping
 * each to `in-progress` and dispatching one job per account. When a wave
 * lands — its days, weekend roll, zone and hour — is decided by
 * `computeNextDeletionWaveAt` and the constants beside it in that
 * codebase's `deletionSchedule.ts`. The lockout is
 * still immediate — only the erasure defers. Admins bypass the wait with
 * the callable's `runNow` flag (legal demands, abuse accounts). The
 * authoritative cron, per-wave cap, and over-age backstop live next to
 * the orchestrator; see that codebase's README.
 *
 * The order of this list is documentation only — nothing compares
 * statuses ordinally.
 */
export const ACCOUNT_DELETION_STATUSES = [
  'scheduled',
  'in-progress',
  'complete',
  'failed',
  'needs-attention',
  'cancelled',
] as const;

/** Zod schema for {@link AccountDeletionStatus}. */
export const AccountDeletionStatusSchema = z.enum(ACCOUNT_DELETION_STATUSES);

/** Lifecycle of a tombstone. See {@link ACCOUNT_DELETION_STATUSES}. */
export type AccountDeletionStatus = z.infer<typeof AccountDeletionStatusSchema>;

/**
 * Who initiated the deletion.
 *
 * - `self` — the account owner, via the delete-my-account page.
 * - `admin` — a staff member acting on a verified out-of-band request.
 */
export const ACCOUNT_DELETION_SOURCES = ['self', 'admin'] as const;

/** Zod schema for {@link AccountDeletionSource}. */
export const AccountDeletionSourceSchema = z.enum(ACCOUNT_DELETION_SOURCES);

/** Who initiated the deletion. See {@link ACCOUNT_DELETION_SOURCES}. */
export type AccountDeletionSource = z.infer<typeof AccountDeletionSourceSchema>;

/**
 * Per-category summary written to the tombstone as the cascade runs:
 * the policy applied and how many ops it took. Never content.
 */
export const CategorySummaryEntrySchema = z.object({
  /** Policy the cascade applied to the category. */
  policy: DeletionPolicySchema,
  /** Number of ops (deletes/updates/object removals) executed. */
  opCount: z.number().int().nonnegative(),
});

/** Per-category summary entry. See {@link CategorySummaryEntrySchema}. */
export type CategorySummaryEntry = z.infer<typeof CategorySummaryEntrySchema>;

/**
 * The tombstone document at `accountDeletions/{uid}`.
 *
 * `.loose()` so adding fields later doesn't break older readers.
 * PII-free by construction: the only identifiers stored are uids — this
 * document's own subject, plus `requestedBy` (the acting admin, for an
 * admin-initiated deletion). No name, email, or content ever goes in
 * this document (see the retention rationale in `classification.ts`
 * under `deletion-tombstone`).
 */
export const AccountDeletionTombstoneSchema = z
  .object({
    /** Uid whose account is being deleted (also the document id). */
    uid: z.string().min(1),
    /** Where the tombstone is in its lifecycle. */
    status: AccountDeletionStatusSchema,
    /** Who initiated the deletion. */
    source: AccountDeletionSourceSchema,
    /**
     * Uid of the requester: the account owner for `self`, the acting
     * admin for `admin`.
     */
    requestedBy: z.string().min(1),
    /** Server time the deletion was requested. */
    requestedAt: FirestoreTimestampSchema,
    /**
     * Earliest deletion wave this tombstone is eligible for, set when the
     * cascade is deferred (`status: 'scheduled'`). Null for a tombstone
     * whose cascade started immediately (an admin run-now request), and
     * set to null when a wave or run-now claims the tombstone or a
     * superadmin cancels it, so it is only ever read on a `scheduled`
     * tombstone. Tombstones written
     * before waves moved to a Pacific working hour hold midnight UTC;
     * they are honoured as written, since dueness only asks whether this
     * instant has passed.
     *
     * Advisory, not load-bearing: the orchestrator also applies an
     * age-based backstop, so a null or malformed value can never park an
     * account outside every future wave. See the orchestrator's
     * selection module.
     */
    scheduledFor: FirestoreTimestampSchema.nullable(),
    /** Server time the cascade reached a terminal status; null until then. */
    completedAt: FirestoreTimestampSchema.nullable(),
    /** True when the job runs in dry-run mode (enumerate, write nothing). */
    dryRun: z.boolean(),
    /** Id of the currently-active job doc, or null when none is running. */
    activeJobId: z.string().nullable(),
    /**
     * Server time the cascade was dispatched — the moment the tombstone
     * became `in-progress` and its job doc was created, whether by a
     * deletion wave's claim or by an admin run-now request. Null while
     * `scheduled`.
     *
     * Distinct from `requestedAt` on purpose. Under batching a request
     * can be weeks older than its dispatch, so "how long has this
     * cascade been running?" can only be asked of this field; asking it
     * of `requestedAt` reports every batched deletion as overdue the
     * instant it starts.
     */
    claimedAt: FirestoreTimestampSchema.nullable(),
    /**
     * Resume checkpoints: manifest task id → server time the task
     * completed. A resumed job skips every task recorded here.
     */
    completedTasks: z.record(z.string(), FirestoreTimestampSchema),
    /**
     * Per-category op summary (categories from
     * `ACCOUNT_DELETION_CLASSIFICATION`). Kinds and counts only.
     */
    categorySummary: z.record(z.string(), CategorySummaryEntrySchema),
    /** Server time the residue scan last verified zero residue; null if never. */
    residueVerifiedAt: FirestoreTimestampSchema.nullable(),
    /**
     * Escape hatch: when true, the cascade refuses to start (set by an
     * admin for accounts subject to an active legal hold).
     */
    legalHold: z.boolean(),
    /**
     * Server time a superadmin cancelled the deletion; set only when
     * `cancelled`.
     */
    cancelledAt: FirestoreTimestampSchema.optional(),
    /** Uid of the superadmin who cancelled; set only when `cancelled`. */
    cancelledBy: z.string().min(1).optional(),
  })
  .loose();

/** The tombstone document. See {@link AccountDeletionTombstoneSchema}. */
export type AccountDeletionTombstone = z.infer<
  typeof AccountDeletionTombstoneSchema
>;

/**
 * Action recorded by an audit event.
 *
 * - `requested` — tombstone created (deletion requested and locked).
 * - `started` — a job invocation began executing tasks.
 * - `task-completed` — one manifest task finished (see `taskId`).
 * - `completed` — the cascade finished and verification passed.
 * - `failed` — the job hit a terminal error.
 * - `residue-found` — a residue scan found leftovers.
 * - `residue-cleared` — a follow-up run removed previously-found residue.
 * - `warning` — a non-fatal condition needing operator awareness (e.g. a
 *   section left leaderless).
 * - `cancelled` — a superadmin cancelled a scheduled deletion and the
 *   account was restored.
 */
export const ACCOUNT_DELETION_AUDIT_ACTIONS = [
  'requested',
  'started',
  'task-completed',
  'completed',
  'failed',
  'residue-found',
  'residue-cleared',
  'warning',
  'cancelled',
] as const;

/** Zod schema for {@link AccountDeletionAuditAction}. */
export const AccountDeletionAuditActionSchema = z.enum(
  ACCOUNT_DELETION_AUDIT_ACTIONS,
);

/** Action recorded by an audit event. See {@link ACCOUNT_DELETION_AUDIT_ACTIONS}. */
export type AccountDeletionAuditAction = z.infer<
  typeof AccountDeletionAuditActionSchema
>;

/**
 * Append-only audit event at
 * `accountDeletions/{uid}/deletionAuditEvents/{eventId}`.
 * Rules forbid client writes entirely and admin-SDK writers never update
 * or delete events. `.loose()` for forward compatibility.
 */
export const AccountDeletionAuditEventSchema = z
  .object({
    /** What happened. */
    action: AccountDeletionAuditActionSchema,
    /** Manifest task id, for `task-completed` and task-scoped warnings. */
    taskId: z.string().optional(),
    /**
     * Short human-readable detail. MUST NOT contain PII — task ids,
     * counts, and category keys only.
     */
    detail: z.string().optional(),
    /**
     * Uid of the admin who asked, on a `requested` event from an admin
     * request or re-run, or on a `cancelled` event. Server-side only: the
     * console never returns it.
     */
    actorUid: z.string().optional(),
    /** Server-assigned write time. */
    serverTimestamp: FirestoreTimestampSchema,
  })
  .loose();

/** A single audit event. See {@link AccountDeletionAuditEventSchema}. */
export type AccountDeletionAuditEvent = z.infer<
  typeof AccountDeletionAuditEventSchema
>;

/**
 * Whether a tombstone read means the account is being, or has been,
 * deleted. Every tombstone does except one whose status is `cancelled`:
 * a deletion cancelled before its cascade ran, whose account was
 * restored. An unreadable status counts as in force, so a garbled
 * document errs toward treating the account as deleted.
 *
 * Use this rather than `snapshot.exists` for "is this account deleted?".
 *
 * @param snapshot - The tombstone read; any Firestore document snapshot.
 * @returns True when the document exists and is not `cancelled`.
 * @throws Never.
 */
export function isAccountDeletionInForce(snapshot: {
  exists: boolean;
  get(fieldPath: string): unknown;
}): boolean {
  return snapshot.exists && snapshot.get('status') !== 'cancelled';
}

/** Top-level tombstone collection. */
export const ACCOUNT_DELETIONS_COLLECTION_PATH = 'accountDeletions' as const;

/** Top-level collection holding async-job docs for deletion runs. */
export const ACCOUNT_DELETION_JOBS_COLLECTION_PATH =
  'accountDeletionJobs' as const;

/**
 * Subcollection of a tombstone holding its append-only audit events.
 *
 * Deliberately not named `events`: several unrelated `events` subcollections
 * already exist (for example under `bandits/{courseId}/experiments/{expId}`),
 * so a generic name would make a `collectionGroup` query over this data
 * ambiguous database-wide.
 */
export const ACCOUNT_DELETION_AUDIT_EVENTS_SUBCOLLECTION =
  'deletionAuditEvents' as const;

/**
 * Constructs the path to a user's deletion tombstone.
 *
 * Document path: `accountDeletions/{uid}`
 *
 * @param uid - The uid whose tombstone is addressed.
 * @returns The fully-qualified Firestore document path.
 */
export function getAccountDeletionTombstonePath({uid}: {uid: string}): string {
  return `${ACCOUNT_DELETIONS_COLLECTION_PATH}/${uid}`;
}

/**
 * Constructs the path to a tombstone's audit-event subcollection.
 *
 * Collection path: `accountDeletions/{uid}/deletionAuditEvents`
 *
 * @param uid - The uid whose audit events are addressed.
 * @returns The fully-qualified Firestore collection path.
 */
export function getAccountDeletionAuditEventsCollectionPath({
  uid,
}: {
  uid: string;
}): string {
  return `${getAccountDeletionTombstonePath({uid})}/${ACCOUNT_DELETION_AUDIT_EVENTS_SUBCOLLECTION}`;
}

/**
 * Constructs the path to a deletion job document.
 *
 * Document path: `accountDeletionJobs/{jobId}`
 *
 * @param jobId - The job document id.
 * @returns The fully-qualified Firestore document path.
 */
export function getAccountDeletionJobDocPath({jobId}: {jobId: string}): string {
  return `${ACCOUNT_DELETION_JOBS_COLLECTION_PATH}/${jobId}`;
}
